import { useEffect, useState } from "react";
import { CalendarClock, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { TIME_SLOTS, humanSlot, SEASON, isInstallDay } from "@/data/collections";
import { getAvailability, rescheduleBooking } from "@/lib/api";
import { toast } from "sonner";

const fmt = (d) => d && `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const Reschedule = ({ booking, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState();
  const [slot, setSlot] = useState(null);
  const [unavailable, setUnavailable] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const iso = fmt(date);
    if (!iso) return;
    getAvailability(iso).then((r) => setUnavailable(r.unavailable_slots || [])).catch(() => setUnavailable([]));
  }, [date]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const defaultMonth = today > SEASON.start ? new Date(today.getFullYear(), today.getMonth(), 1) : SEASON.start;

  const submit = async () => {
    if (!date || !slot) return toast.error("Pick a new date and time");
    setBusy(true);
    try {
      const updated = await rescheduleBooking({ email: booking.email, reference: booking.reference, date: fmt(date), time_slot: slot });
      toast.success("New date proposed — we'll confirm by email");
      onUpdated(updated);
      setOpen(false); setDate(undefined); setSlot(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reschedule");
    } finally {
      setBusy(false);
    }
  };

  if (booking.status === "denied") return null;

  return (
    <div className="p-8 border-t" style={{ borderColor: "var(--pp-border)" }} data-testid="reschedule-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="pp-caption mb-1">Need a different day?</div>
          <p className="text-sm" style={{ color: "var(--pp-text-muted)" }}>Propose a new install window and we'll confirm it by email.</p>
        </div>
        <button data-testid="reschedule-toggle" onClick={() => setOpen((v) => !v)} className="pp-btn-ghost" style={{ color: "var(--pp-terracotta)" }}>
          <CalendarClock className="h-4 w-4" /> {open ? "Cancel" : "Propose a new date"}
        </button>
      </div>
      {open && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border p-2 inline-block self-start" style={{ borderColor: "var(--pp-border)" }} data-testid="reschedule-calendar">
            <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setSlot(null); }}
              defaultMonth={date || defaultMonth} fromMonth={SEASON.start} toMonth={SEASON.end}
              disabled={(d) => !isInstallDay(d)} />
          </div>
          <div>
            <div className="grid grid-cols-2 gap-2">
              {TIME_SLOTS.map((s) => {
                const taken = unavailable.includes(s);
                const active = slot === s;
                return (
                  <button key={s} data-testid={`reschedule-slot-${s.replace(":", "")}`} disabled={!date || taken} onClick={() => setSlot(s)}
                    className="py-3 text-sm border disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ backgroundColor: active ? "var(--pp-terracotta)" : "transparent", color: active ? "var(--pp-parchment)" : "var(--pp-text)", borderColor: active ? "var(--pp-terracotta)" : "var(--pp-border)", textDecoration: taken ? "line-through" : "none" }}>
                    {humanSlot(s)}
                  </button>
                );
              })}
            </div>
            <button data-testid="reschedule-submit" onClick={submit} disabled={busy || !date || !slot} className="pp-btn mt-5 w-full">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send request
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
